import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Package, CheckCircle2, Clock, Truck, Home, ArrowLeft, FileText } from "lucide-react";
import { API } from "../constants";
import { useAuth } from "../context/AuthContext";

// Standard purchase pipeline + wholesale invoice pipeline
const RETAIL_STAGES = [
  { key: "pending", label: "Order Placed", icon: Clock },
  { key: "confirmed", label: "Confirmed", icon: CheckCircle2 },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "delivered", label: "Delivered", icon: Home },
];

const WHOLESALE_STAGES = [
  { key: "confirmed", label: "Invoice Issued", icon: FileText },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "delivered", label: "Delivered", icon: Home },
];

const OrderTrackingPage = () => {
  const { orderId } = useParams();
  const { token, user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/orders/${orderId}`, { headers });
        setOrder(response.data);
      } catch (err) {
        setError(err?.response?.data?.detail || "Order not found");
      } finally {
        setLoading(false);
      }
    };
    if (orderId) fetchOrder();
  }, [orderId, token]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 px-4">
        <div className="max-w-4xl mx-auto py-16 text-center">
          <p className="text-gray-400 mb-4">Please log in to view your order.</p>
          <Link to="/login" className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-6 py-3 rounded-sm font-bold uppercase tracking-wider">
            Log In
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24">
        <div className="text-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D62828] mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 px-4">
        <div className="max-w-4xl mx-auto py-16 text-center">
          <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg mb-6">{error || "Order not found"}</p>
          <Link to="/orders" className="inline-flex items-center gap-2 text-[#D62828] hover:text-white" data-testid="back-to-orders">
            <ArrowLeft className="w-4 h-4" /> Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  const isWholesale = order.is_wholesale_order || order.payment_method === "invoice";
  const stages = isWholesale ? WHOLESALE_STAGES : RETAIL_STAGES;
  const currentIdx = stages.findIndex((s) => s.key === order.order_status);
  // If status is something not in the pipeline (e.g. 'cancelled'), mark all as inactive
  const effectiveIdx = currentIdx >= 0 ? currentIdx : -1;

  // Build a lookup: when did each status transition happen?
  const historyMap = {};
  (order.status_history || []).forEach((h) => {
    if (h && h.status) historyMap[h.status] = h.timestamp;
  });
  // Fall back to created_at for the first stage if no history yet
  if (!historyMap[stages[0].key]) historyMap[stages[0].key] = order.created_at;

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16" data-testid="order-tracking-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/orders" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-6" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> All Orders
        </Link>

        {/* Header */}
        <div className="bg-[#121212] border border-white/10 rounded-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Order</p>
              <p className="text-white font-heading text-2xl tracking-wide" data-testid="order-id">#{order.order_id}</p>
              <p className="text-gray-500 text-xs mt-1">Placed {fmtDate(order.created_at)}</p>
            </div>
            <div className="text-right">
              <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Total</p>
              <p className="text-[#D62828] font-bold text-2xl" data-testid="order-total">${order.total.toFixed(2)}</p>
              {isWholesale && order.payment_due_at && (
                <p className="text-[#D4AF37] text-xs mt-1">NET-30 due {new Date(order.payment_due_at).toLocaleDateString()}</p>
              )}
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-[#121212] border border-white/10 rounded-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl tracking-wide">TRACKING</h2>
            {order.tracking_number && (
              <a
                href={`https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(order.tracking_number)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#D62828] hover:bg-[#B91C1C] text-white text-xs uppercase tracking-wide font-bold px-3 py-2 rounded-sm"
                data-testid="tracking-link"
              >
                Track on {order.tracking_carrier || 'FedEx'} →
              </a>
            )}
          </div>
          {order.tracking_number && (
            <p className="text-gray-400 text-xs mb-4">
              {order.tracking_carrier || 'FedEx'} tracking: <span className="text-white font-mono">{order.tracking_number}</span>
            </p>
          )}
          <div className="relative" data-testid="order-timeline">
            {/* progress line */}
            <div className="absolute left-6 top-6 bottom-6 w-px bg-white/10" aria-hidden="true"></div>
            <div className="space-y-6">
              {stages.map((stage, idx) => {
                const isDone = effectiveIdx >= 0 && idx <= effectiveIdx;
                const isCurrent = idx === effectiveIdx;
                const Icon = stage.icon;
                const stamp = historyMap[stage.key];
                return (
                  <div key={stage.key} className="relative flex items-start gap-4" data-testid={`timeline-stage-${stage.key}`}>
                    <div
                      className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center border-2 transition-colors ${
                        isDone
                          ? "bg-[#D62828] border-[#D62828] text-white"
                          : "bg-[#050505] border-white/20 text-gray-600"
                      } ${isCurrent ? "ring-4 ring-[#D62828]/30" : ""}`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 pt-2">
                      <p className={`font-semibold text-sm ${isDone ? "text-white" : "text-gray-500"}`}>
                        {stage.label}
                      </p>
                      {isDone && stamp && (
                        <p className="text-gray-500 text-xs mt-0.5">{fmtDate(stamp)}</p>
                      )}
                      {isCurrent && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-[#D62828]/20 text-[#D62828] rounded-sm text-xs font-bold uppercase tracking-wide">
                          Current
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {order.order_status === "cancelled" && (
                <div className="relative flex items-start gap-4">
                  <div className="relative z-10 w-12 h-12 rounded-full flex items-center justify-center bg-red-500/20 border-2 border-red-500 text-red-500">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div className="flex-1 pt-2">
                    <p className="font-semibold text-sm text-red-400">Cancelled</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="bg-[#121212] border border-white/10 rounded-sm p-6 mb-6">
          <h2 className="font-heading text-xl tracking-wide mb-4">ITEMS</h2>
          <div className="space-y-3" data-testid="order-items">
            {(order.items || []).map((item) => (
              <div key={item.product_id} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
                <div>
                  <p className="text-white text-sm font-medium">{item.name}</p>
                  <p className="text-gray-500 text-xs">Qty {item.quantity}</p>
                </div>
                <p className="text-gray-300 text-sm">${(item.subtotal || item.price * item.quantity).toFixed(2)}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 mt-4 pt-4 space-y-1 text-sm">
            <div className="flex justify-between text-gray-400">
              <span>Subtotal</span>
              <span>${order.subtotal.toFixed(2)}</span>
            </div>
            {order.promo_discount > 0 && (
              <div className="flex justify-between text-[#D4AF37]">
                <span>Discount {order.effective_discount_percent ? `(${order.effective_discount_percent}%)` : ""}</span>
                <span>-${order.promo_discount.toFixed(2)}</span>
              </div>
            )}
            {order.store_credit_applied > 0 && (
              <div className="flex justify-between text-[#D4AF37]">
                <span>Store Credit</span>
                <span>-${order.store_credit_applied.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-gray-400">
              <span>Tax</span>
              <span>${order.tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-gray-400">
              <span>Shipping</span>
              <span>{order.shipping === 0 ? "FREE" : `$${order.shipping.toFixed(2)}`}</span>
            </div>
            <div className="flex justify-between text-white font-bold text-base pt-2 border-t border-white/10">
              <span>Total</span>
              <span className="text-[#D62828]">${order.total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Shipping address */}
        {order.shipping_address && Object.keys(order.shipping_address).length > 0 && (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
            <h2 className="font-heading text-xl tracking-wide mb-3">SHIPPING TO</h2>
            <p className="text-gray-300 text-sm" data-testid="order-shipping-address">
              {order.shipping_address.name && <>{order.shipping_address.name}<br /></>}
              {order.shipping_address.street && <>{order.shipping_address.street}<br /></>}
              {[order.shipping_address.city, order.shipping_address.state, order.shipping_address.zip].filter(Boolean).join(", ")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTrackingPage;
